import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type Month = {
  name: string;
  value: string;
};

export class DateUtils {
  static getMonthsSelectList = (options: { minimalMonth?: number; maximalMonth?: number; locale?: string } = {}) => {
    const { minimalMonth = 1, maximalMonth = 12, locale = 'en' } = options;

    dayjs.locale(locale);

    return Array.from({ length: 12 }, (_, i) => {
      const date = dayjs().month(i);
      return {
        label: date.format('MMMM'),
        value: String(i + 1).padStart(2, '0'),
      };
    }).filter(({ value }) => {
      const num = parseInt(value, 10);
      return num >= minimalMonth && num <= maximalMonth;
    });
  };

  static getLastMonths = (options: { locale?: string } = {}) => {
    const dates: Month[] = [];

    const startDate = dayjs('2024-01-01');
    const today = dayjs();
    const monthCount = today.diff(startDate, 'month') + 1;

    for (let i = 0; i < monthCount; i++) {
      const date = startDate
        .add(i, 'month')
        .locale(options.locale || 'en')
        .date(2);
      const monthName = date.format('MMMM');

      dates.push({ name: monthName, value: this.formatISODate(date.toDate())! });
    }

    return dates.reverse();
  };

  static getCurrentMonth = (options: { locale?: string } = {}) => {
    dayjs.locale(options.locale || 'en');
    return dayjs().format('MMMM');
  };

  static getYearsList = (startYear: number = 2000) => {
    const result = [];
    const currentYear = dayjs().year();

    for (let i = startYear; i <= currentYear; i++) result.push(i.toString());

    return result;
  };

  static formatISODate = (date?: Date) => {
    if (!date) return undefined;

    const d = dayjs(date);
    if (!d.isValid()) return undefined;
    return d.utc().format('YYYY-MM-DD');
  };

  static formatDate = (date?: string, options?: { withTime?: boolean; adjustTime?: boolean }) => {
    if (!date) return '';

    let formattedDate = dayjs(date);

    if (options?.adjustTime) formattedDate = formattedDate.subtract(2, 'hour');

    const pattern = `YYYY-MM-DD${options?.withTime ? ' HH:mm' : ''}`;
    return formattedDate.format(pattern);
  };

  static formatToIsoString = (date: Dayjs) => date.utcOffset(0, true).startOf('day').toISOString();

  static getFirstDateOfYear = (year?: string | number, month?: string | number): string =>
    dayjs(`${year}-${month}-01`).startOf('month').utcOffset(0, true).toISOString();

  static getLastDateOfYear = (year?: string | number, month?: string | number): string =>
    dayjs(`${year}-${month}-01`).endOf('month').utcOffset(0, true).toISOString();

  static getCurrentDateUTC = () => dayjs().utc().toISOString();
}
